import React from 'react';
import { Users, Building, Award, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, certifications } from '../mock/data';

const About = () => {
  const milestones = [
    { year: "2003", event: "First Cuisine Food Industries established", company: "First Cuisine" },
    { year: "2009", event: "PS Catering commenced operations", company: "PS Catering" },
    { year: "2014", event: "FC Food Industries founded", company: "FC Food Industries" },
    { year: "2022", event: "Teng Food Holdings established as holding company", company: "Teng Food Holdings" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Teng Food Holdings</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              A leading food manufacturing group with over 20 years of combined expertise 
              in halal and non-halal food production, catering, and innovative culinary solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Teng Food Holdings was established in July 2022 as the culmination of decades of 
                  experience and expertise in Singapore's food manufacturing industry. Our journey 
                  began with First Cuisine Food Industries in 2003, pioneering halal food manufacturing 
                  with a commitment to quality and innovation.
                </p>
                <p>
                  Over the years, we expanded our capabilities through PS Catering (2009) and 
                  FC Food Industries (2014), each bringing specialized expertise in non-halal 
                  manufacturing and value-added food production respectively.
                </p>
                <p>
                  Today, as a unified holding company, we leverage our collective strengths to 
                  deliver comprehensive food manufacturing solutions across multiple market segments, 
                  serving both business and consumer markets with unwavering commitment to excellence.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Food Manufacturing Facility"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Food & Packaging Showcase */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Culinary Excellence
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From gourmet preparations to innovative packaging solutions, we deliver quality 
              and innovation across every aspect of food manufacturing and presentation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Gourmet Food Preparation */}
            <div className="group">
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1695606452859-d1103fba6a14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwc2VhZm9vZCUyMGRpc2h8ZW58MHx8fHwxNzYyMDU2MTE5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Gourmet seafood preparation showcasing culinary expertise"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">
                Gourmet Preparations
              </h3>
              <p className="text-gray-600">
                Expertly crafted dishes showcasing our culinary artistry and attention to detail
              </p>
            </div>

            {/* Asian Cuisine Expertise */}
            <div className="group">
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img 
                  src="https://images.pexels.com/photos/5848483/pexels-photo-5848483.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                  alt="Asian cuisine specialties with authentic flavors and presentation"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">
                Asian Specialties
              </h3>
              <p className="text-gray-600">
                Authentic flavors and traditional techniques meeting modern culinary standards
              </p>
            </div>

            {/* Commercial Packaging */}
            <div className="group">
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1671538174996-0ee4572830df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwZm9vZCUyMHBhY2thZ2luZ3xlbnwwfHx8fDE3NjIwNTYxMTl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional food packaging and delivery solutions"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">
                Professional Packaging
              </h3>
              <p className="text-gray-600">
                Innovative packaging solutions ensuring freshness and quality from kitchen to table
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Journey</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Key milestones in building Singapore's leading food manufacturing group
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-blue-200 transform md:-translate-x-0.5"></div>
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex items-center">
                  <div className="flex items-center w-full">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full border-4 border-white shadow-lg z-10 md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                      <Calendar className="h-4 w-4 text-white m-0.5" />
                    </div>
                    
                    <div className={`ml-6 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8 md:ml-auto'}`}>
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-2xl font-bold text-blue-600">{milestone.year}</span>
                            <span className="text-sm text-gray-500">•</span>
                            <span className="text-sm font-medium text-gray-700">{milestone.company}</span>
                          </div>
                          <p className="text-gray-600">{milestone.event}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Structure */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Corporate Structure</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A unified holding company structure that combines specialized expertise 
              across multiple food manufacturing sectors
            </p>
          </div>

          {/* Holding Company */}
          <div className="text-center mb-12">
            <Card className="inline-block bg-gradient-to-r from-blue-600 to-red-600 text-white">
              <CardContent className="p-8">
                <Building className="h-12 w-12 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">{companyInfo.holdingCompany.name}</h3>
                <p className="text-blue-100">UEN: {companyInfo.holdingCompany.uen}</p>
                <p className="text-blue-100">Established: {companyInfo.holdingCompany.established}</p>
              </CardContent>
            </Card>
          </div>

          {/* Subsidiaries */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Object.values(companyInfo.subsidiaries).map((subsidiary, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-blue-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{subsidiary.name}</h4>
                  <div className="text-sm text-blue-600 font-medium mb-2">{subsidiary.type}</div>
                  <div className="text-sm text-gray-500 mb-3">
                    UEN: {subsidiary.uen}<br />
                    Est. {subsidiary.established}
                  </div>
                  <p className="text-sm text-gray-600">{subsidiary.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Certifications & Compliance</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Maintaining the highest standards through rigorous certifications 
              and compliance with regulatory requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <Award className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">{cert.name}</h4>
                  <div className="text-sm text-blue-600 font-medium mb-2">{cert.category}</div>
                  <p className="text-sm text-gray-600">{cert.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;